import { Center, HStack, Text } from '@chakra-ui/react';
import { ComponentType, Ref } from 'react';
import Select, { GroupBase, MultiValueRemoveProps, OptionProps, Props, SelectInstance, StylesConfig, ValueContainerProps, components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Icon, IconKey } from './Icon';

export type SelectOption<T> = {
  label: string;
  value: T;
  sublabel?: string;
  isDisabled?: boolean;
  img?: string;
};

type SearchSelectProps<T> = Props<SelectOption<T>, false> & {
  selectRef?: Ref<SelectInstance<SelectOption<T>, false>>;
};

function customStyles<T, IsMulti extends boolean = false>(): StylesConfig<SelectOption<T>, IsMulti> {
  return {
    control: (provided) => ({
      ...provided,
      border: '1px solid var(--chakra-colors-gray-200)',
      borderRadius: '4px',
      boxShadow: 'initial',
      minHeight: '32px',
      padding: '4px',
      ':hover': {
        border: '1px solid var(--chakra-colors-gray-300)'
      },
      ':focus-within': {
        zIndex: 1,
        borderColor: 'var(--chakra-colors-blue-500)',
        boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
      }
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      background: 'none'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'var(--chakra-colors-black)',
      padding: '0 8px',
      svg: {
        width: '12px',
        height: '12px'
      },
    }),
    menuList: (provided) => ({
      ...provided,
      boxSizing: 'border-box',
      overflowX: 'hidden',
      padding: '0',
    }),
    multiValue: (provided) => ({
      ...provided,
      alignItems: 'center',
      background: '#F0F5FA',
      borderRadius: '10px',
      boxSizing: 'border-box',
      display: 'inline-flex',
      gap: '4px',
      height: '16px',
      justifyContent: 'center',
      margin: '2px 4px',
      outline: '1px solid rgba(0, 0, 0, 0.15)',
      outlineOffset: '-1px',
      paddingLeft: '6px',
      paddingRight: '6px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      borderRadius: '0px',
      color: 'black',
      fontSize: '10px',
      fontWeight: 500,
      letterSpacing: '0.20px',
      lineHeight: '12px',
      padding: '0px',
      paddingLeft: '0px',
      paddingRight: '0px',
      textTransform: 'uppercase',
      wordWrap: 'break-word',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      alignItems: 'center',
      borderRadius: '0px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      padding: '0px',
      paddingLeft: '0px',
      paddingRight: '0px',
      ':hover': {
        backgroundColor: 'transparent',
        color: 'black',
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--chakra-colors-gray-400)',
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 8px'
    }),
    option: (provided, { isDisabled, isFocused, isSelected }) => ({
      ...provided,
      backgroundColor: isFocused || isSelected ? 'var(--chakra-colors-gray-50)' : 'transparent',
      boxSizing: 'border-box',
      color: isDisabled ? 'var(--chakra-colors-gray-400)' : 'black',
      lineHeight: '24px',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ':active': { backgroundColor: 'var(--chakra-colors-gray-100)' }
    }),
    singleValue: (provided, { selectProps }) => ({
      ...provided,
      display: selectProps.menuIsOpen ? 'none' : 'block',
      margin: 0,
      padding: 0,
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
  };
}

function searchIconStyles<T>(): StylesConfig<SelectOption<T>, false> {
  return {
    ...customStyles<T, false>(),
    control: (provided) => ({
      ...provided,
      border: '1px solid var(--chakra-colors-gray-200)',
      boxShadow: 'initial',
      borderRadius: '4px',
      minHeight: '32px',
      fontWeight: 500,
      ':hover': { borderColor: 'var(--chakra-colors-gray-300)' }
    }),
    option: (provided, { isFocused, isSelected }) => ({
      ...provided,
      fontWeight: 500,
      color: 'black',
      backgroundColor: isFocused || isSelected ? 'var(--chakra-colors-gray-50)' : 'transparent',
      ':active': { backgroundColor: 'var(--chakra-colors-gray-100)' }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px 0 40px'
    }),
    input: (provided) => ({
      ...provided,
      margin: 0
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--chakra-colors-gray-400)',
      marginLeft: 0,
      marginRight: 0
    }),
    singleValue: (provided, { selectProps }) => ({
      ...provided,
      marginLeft: 0,
      marginRight: 0,
      display: selectProps.menuIsOpen ? 'none' : 'block'
    }),
  };
}

function multiCustomStyles<T>(): StylesConfig<SelectOption<T>, true> {
  return {
    ...customStyles<T, true>(),
    input: (provided) => ({
      ...provided,
      padding: '0 4px',
      lineHeight: '16px',
      margin: '0px 2px'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--chakra-colors-gray-400)',
      padding: '0 4px',
      lineHeight: '16px',
      margin: 0
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0px'
    }),
  };
}

export function Option<T>(props: OptionProps<SelectOption<T>>) {
  return (
    <components.Option {...props}>
      <HStack spacing={2} px={1}>
        {
          props.data.img &&
            <Center
              minW={8}
              minH={8}
              bgColor="gray.50"
              borderRadius="2px"
              bgImage={`linear-gradient(rgba(30, 0, 45, 0.30), rgba(30, 0, 45, 0.30)), url('${props.data.img}')`}
              bgPosition="center"
              bgSize="cover"
            />
        }

        <Text
          fontSize={14}
          lineHeight="24px"
          flex="1"
          minW={0}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          { props.data.label }
        </Text>

        {
          props.data.sublabel &&
            <Text
              fontSize={14}
              lineHeight="24px"
              as="i"
              flex="0 1 40%"
              ml="auto"
              minW={0}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              { props.data.sublabel }
            </Text>
        }
      </HStack>
    </components.Option>
  );
}

function ValueContainer({ children, ...props }: ValueContainerProps & { selectProps: Props & { icon: IconKey } }) {
  return (
    <components.ValueContainer {...props}>
      <Icon color="gray.400" icon={props.selectProps.icon} size={4} style={{ position: 'absolute', left: 12 }} />
      { children }
    </components.ValueContainer>
  );
}

function MultiValueRemove(props: MultiValueRemoveProps) {
  return (
    <components.MultiValueRemove {...props}>
      <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect y="0.732239" width="1" height="7" transform="rotate(-45 0 0.732239)" fill="black"/>
        <rect x="5" width="1" height="7" transform="rotate(45 5 0)" fill="black"/>
      </svg>
    </components.MultiValueRemove>
  );
}

export function SearchSelect<T>({ selectRef, ...props }: SearchSelectProps<T>) {
  const components = props.components || { Option };
  const styles = props.styles ? { ...customStyles<T>(), ...props.styles } : customStyles<T>();
  return <Select {...props} ref={selectRef} styles={styles} components={components} />;
}

export function SearchIconSelect<T>(props: Props<{ label: string, value: T, img?: string }, false, GroupBase<{ label: string, value: T, img?: string }>> & { icon: IconKey }) {
  return <Select styles={searchIconStyles<T>()} components={{ Option, ValueContainer: ValueContainer as ComponentType<ValueContainerProps<{ label: string, value: T, img?: string }, false, GroupBase<{ label: string, value: T, img?: string }>>>, IndicatorsContainer: () => null }} {...props} />;
}

export function MultiSelect<T>(props: Props<{ label: string, value: T }>) {
  return <Select isMulti styles={multiCustomStyles<T>()} components={{ MultiValueRemove }} {...props} />;
}

export function MultiCreatableSelect<T>(props: Props<{ label: string, value: T }>) {
  return <CreatableSelect isMulti styles={multiCustomStyles<T>()} components={{ MultiValueRemove, DropdownIndicator: null }} {...props} />;
}